/**
 * Where the camera lands when the user switches camera mode.
 *
 * The viewer used to answer this with two independent resets: the position sync
 * in `Controls` reloaded the sessionStorage snapshot whenever R3F swapped the
 * camera object (up to 500 ms stale, and carrying the *other* projection's
 * `distance`/`zoom`), and a separate effect then dollied to the new mode's
 * hardcoded default — 1000 m for ortho, 50 m for perspective. Two corrections
 * fighting over one frame, neither of which knew what the user was looking at,
 * which is what "a kamera el tud úszni" describes.
 *
 * One rule replaces both: the view direction never changes across a switch, and
 * the visible extent at the orbit target is carried over. The two projections
 * express that extent differently — a perspective camera through its distance
 * (`2·d·tan(fov/2)`), an orthographic one through its zoom
 * (`frustumHeight / zoom`) — so a transition is a conversion between the two,
 * not a reset.
 *
 * Every transition is composed through third person, the one mode that has both
 * a real orbit target and a real distance. First person is third person with the
 * radius collapsed onto the camera; ortho is third person with the distance
 * pinned and the extent moved into the zoom. That keeps the six directed
 * transitions down to one canonical form and makes them round-trip.
 */
import type { CameraMode } from './cameraMode';

export type Vec3 = [number, number, number];

/** Orbit radius in first person. Matches `Controls`' `dollyTo(0.00001)`. */
export const FIRST_PERSON_DISTANCE = 0.00001;

/** Default orbit radius when entering third person from first person. */
export const THIRD_PERSON_DISTANCE = 50;

/**
 * Fixed orbit radius in ortho. The screen-space sizing rule in CLAUDE.md builds
 * on the ortho camera being dollied far out, and in an orthographic projection
 * the distance does not affect framing at all — the zoom does.
 */
export const ORTHO_DISTANCE = 1000;

/** `minDistance` / `maxDistance` of the third-person `CameraControls`. */
const MIN_PERSPECTIVE_DISTANCE = 0.5;
const MAX_PERSPECTIVE_DISTANCE = 10_000;

/** `minZoom` / `maxZoom` of the orthographic `CameraControls`. */
const MIN_ORTHO_ZOOM = 1;
const MAX_ORTHO_ZOOM = 500;

export interface CameraOptics {
  /** Vertical field of view of the perspective camera, degrees. */
  fovDeg: number;
  /** `top - bottom` of the orthographic frustum at zoom 1, world units. */
  orthoFrustumHeight: number;
}

export interface CameraPose {
  position: Vec3;
  /** The orbit target. Sits on the camera itself in first person. */
  target: Vec3;
  /** Camera look direction. Need not be normalized. */
  forward: Vec3;
  /** Orthographic zoom; 1 in perspective. */
  zoom: number;
}

export interface CameraTransition {
  position: Vec3;
  target: Vec3;
  zoom: number;
  /** `|target - position|`, i.e. what to `dollyTo`. */
  distance: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

const normalize = ([x, y, z]: Vec3): Vec3 => {
  const len = Math.hypot(x, y, z);
  // A zero direction can only come from a camera that has not been placed yet;
  // looking down keeps the arithmetic finite until the next frame fixes it.
  if (len === 0) return [0, 0, -1];
  return [x / len, y / len, z / len];
};

const along = (from: Vec3, dir: Vec3, d: number): Vec3 => [from[0] + dir[0] * d, from[1] + dir[1] * d, from[2] + dir[2] * d];

/** Visible world height of a perspective camera at `distance`. */
const perspectiveHeight = (distance: number, fovDeg: number) => 2 * distance * Math.tan((fovDeg * Math.PI) / 360);

/**
 * The pose expressed as third person would hold it: an orbit target plus the
 * perspective distance that shows the same extent.
 */
function toOrbit(mode: CameraMode, pose: CameraPose, forward: Vec3, optics: CameraOptics): { target: Vec3; distance: number } {
  if (mode === 'FIRST_PERSON') {
    // The walker keeps standing where they are; the orbit opens up in front of
    // them. Pulling the camera backwards instead — which is what `dollyTo(50)`
    // did — moves the picture, and that is the jump users report.
    return { target: along(pose.position, forward, THIRD_PERSON_DISTANCE), distance: THIRD_PERSON_DISTANCE };
  }
  if (mode === 'ORTHO') {
    const visibleHeight = optics.orthoFrustumHeight / Math.max(pose.zoom, 1e-6);
    const distance = visibleHeight / (2 * Math.tan((optics.fovDeg * Math.PI) / 360));
    return { target: pose.target, distance: clamp(distance, MIN_PERSPECTIVE_DISTANCE, MAX_PERSPECTIVE_DISTANCE) };
  }
  const live = Math.hypot(
    pose.position[0] - pose.target[0],
    pose.position[1] - pose.target[1],
    pose.position[2] - pose.target[2],
  );
  return {
    target: pose.target,
    distance: clamp(live || THIRD_PERSON_DISTANCE, MIN_PERSPECTIVE_DISTANCE, MAX_PERSPECTIVE_DISTANCE),
  };
}

/**
 * The camera pose to apply after switching from `from` to `to`.
 *
 * `forward` is the camera's current look direction and is preserved exactly —
 * a mode switch never rotates the view.
 */
export function cameraTransition(from: CameraMode, to: CameraMode, pose: CameraPose, optics: CameraOptics): CameraTransition {
  const forward = normalize(pose.forward);
  const orbit = toOrbit(from, pose, forward, optics);
  const eye = along(orbit.target, forward, -orbit.distance);

  if (to === 'FIRST_PERSON') {
    // The camera stays on the orbit's eye point and the target collapses onto
    // it, so the picture is identical either side of the switch.
    return { position: eye, target: along(eye, forward, FIRST_PERSON_DISTANCE), zoom: 1, distance: FIRST_PERSON_DISTANCE };
  }

  if (to === 'ORTHO') {
    const zoom = clamp(
      optics.orthoFrustumHeight / perspectiveHeight(orbit.distance, optics.fovDeg),
      MIN_ORTHO_ZOOM,
      MAX_ORTHO_ZOOM,
    );
    return { position: along(orbit.target, forward, -ORTHO_DISTANCE), target: orbit.target, zoom, distance: ORTHO_DISTANCE };
  }

  return { position: eye, target: orbit.target, zoom: 1, distance: orbit.distance };
}
